/**
 * SubD kernel — Catmull-Clark subdivision (R3).
 *
 * Generalized for triangle + quad cages (face points average every face
 * corner). A single `creaseWeight` in [0,1] sharpens every edge toward its
 * midpoint — the functional equivalent of 3D's per-edge crease set for
 * the cage shapes the SubD commands can build (box/plane/loft), consumed
 * by the renderer on display. The cage is the source of truth; the smooth
 * limit mesh is derived (and cached by the renderer).
 */

import type { Pt3 } from '../../commands/geometryMath';

export interface SubdCage {
    vertices: Pt3[];
    faces: number[][];
}

/** One Catmull-Clark refinement pass. crease (0..1) sharpens all edges:
 *  0 = pure smooth limit, 1 = pure midpoint split (cage preserved). */
export function catmullClarkStep(cage: SubdCage, crease = 0): SubdCage {
    const { vertices, faces } = cage;
    const nV = vertices.length;
    const out: Pt3[] = [];
    const facePoint = (f: number[]): Pt3 => {
        const c: Pt3 = [0, 0, 0];
        f.forEach((i) => { c[0] += vertices[i][0]; c[1] += vertices[i][1]; c[2] += vertices[i][2]; });
        const n = f.length;
        return [c[0] / n, c[1] / n, c[2] / n];
    };
    // 1) face points
    const fp: number[] = faces.map((f) => { out.push(facePoint(f)); return out.length - 1; });
    // 2) edge points (regular CC blend vs midpoint by crease)
    const edgeKey = (a: number, b: number) => (a < b ? `${a}:${b}` : `${b}:${a}`);
    const edgeFaces = new Map<string, number[]>();
    faces.forEach((f, fi) => {
        for (let i = 0; i < f.length; i++) {
            const a = f[i], b = f[(i + 1) % f.length];
            const k = edgeKey(a, b);
            edgeFaces.set(k, [...(edgeFaces.get(k) ?? []), fi]);
        }
    });
    const ep = new Map<string, number>();
    edgeFaces.forEach((fs, k) => {
        const [a, b] = k.split(':').map(Number);
        const va = vertices[a], vb = vertices[b];
        const mid: Pt3 = [(va[0] + vb[0]) / 2, (va[1] + vb[1]) / 2, (va[2] + vb[2]) / 2];
        let p = mid;
        if (fs.length === 2) {
            const f1 = out[fp[fs[0]]], f2 = out[fp[fs[1]]];
            const cc: Pt3 = [
                (va[0] * 3 + vb[0] * 3 + f1[0] + f2[0]) / 8,
                (va[1] * 3 + vb[1] * 3 + f1[1] + f2[1]) / 8,
                (va[2] * 3 + vb[2] * 3 + f1[2] + f2[2]) / 8,
            ];
            p = lerp3(mid, cc, 1 - crease);
        } else {
            // boundary edge: half-edge rule, still crease-blended toward mid
            const f = out[fp[fs[0]]];
            const cc: Pt3 = [
                (va[0] + vb[0]) * 0.25 + f[0] * 0.5,
                (va[1] + vb[1]) * 0.25 + f[1] * 0.5,
                (va[2] + vb[2]) * 0.25 + f[2] * 0.5,
            ];
            p = lerp3(mid, cc, 1 - crease);
        }
        out.push(p);
        ep.set(k, out.length - 1);
    });
    // 3) new original vertices
    const vNeighbors = new Map<number, { edges: [number, number][]; faces: number[] }>();
    faces.forEach((f, fi) => f.forEach((vi) => {
        const rec = vNeighbors.get(vi) ?? { edges: [], faces: [] };
        rec.faces.push(fi);
        vNeighbors.set(vi, rec);
    }));
    edgeFaces.forEach((_fs, k) => {
        const [a, b] = k.split(':').map(Number);
        (vNeighbors.get(a)?.edges ?? []).push([a, b]);
        (vNeighbors.get(b)?.edges ?? []).push([b, a]);
    });
    const oldToNew = new Map<number, number>();
    for (let v = 0; v < nV; v++) {
        const rec = vNeighbors.get(v);
        const n = rec?.faces.length ?? 0;
        const p = vertices[v];
        if (n < 3 || (rec && isBoundaryVertex(rec))) {
            // boundary/corner vertex: keep (crease=0 still smooths along
            // boundary via the half-edge rule; corners stay put like 3D)
            out.push([...p]);
        } else {
            const fAvg: Pt3 = [0, 0, 0];
            rec!.faces.forEach((fi) => { const fpP = out[fp[fi]]; fAvg[0] += fpP[0] / n; fAvg[1] += fpP[1] / n; fAvg[2] += fpP[2] / n; });
            const eAvg: Pt3 = [0, 0, 0];
            rec!.edges.forEach(([a, b]) => {
                const eid = ep.get(edgeKey(a, b));
                if (eid === undefined) return;
                const e = out[eid];
                eAvg[0] += e[0] / rec!.edges.length; eAvg[1] += e[1] / rec!.edges.length; eAvg[2] += e[2] / rec!.edges.length;
            });
            const cc: Pt3 = [
                (fAvg[0] + 2 * eAvg[0] + (n - 3) * p[0]) / n,
                (fAvg[1] + 2 * eAvg[1] + (n - 3) * p[1]) / n,
                (fAvg[2] + 2 * eAvg[2] + (n - 3) * p[2]) / n,
            ];
            out.push(lerp3(p, cc, 1 - crease));
        }
        oldToNew.set(v, out.length - 1);
    }
    // 4) new faces: for each old face, fan face-point -> edge-points -> verts
    const newFaces: number[][] = [];
    faces.forEach((f, fi) => {
        const fpIdx = fp[fi];
        const k = f.length;
        for (let i = 0; i < k; i++) {
            const v = f[i];
            const ePrev = edgeKey(f[(i - 1 + k) % k], v);
            const eNext = edgeKey(v, f[(i + 1) % k]);
            const epPrev = ep.get(ePrev);
            const epNext = ep.get(eNext);
            const vn = oldToNew.get(v);
            if (epPrev === undefined || epNext === undefined || vn === undefined) continue;
            newFaces.push([fpIdx, epNext, vn, epPrev]);
        }
    });
    return { vertices: out, faces: newFaces };
}

function isBoundaryVertex(rec: { edges: [number, number][]; faces: number[] }): boolean {
    // a vertex whose face fan is open: count distinct edges; for a closed
    // fan the edge count equals the face count, boundary has one extra
    const seen = new Set<string>();
    rec.edges.forEach(([a, b]) => seen.add(a < b ? `${a}:${b}` : `${b}:${a}`));
    return seen.size > rec.faces.length;
}

function lerp3(a: Pt3, b: Pt3, t: number): Pt3 {
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/** Run `level` CC passes (clamped 1..4 for display cost). */
export function subdLimit(cage: SubdCage, level = 2, crease = 0): SubdCage {
    let cur = cage;
    const l = Math.max(0, Math.min(4, Math.round(level)));
    for (let i = 0; i < l; i++) cur = catmullClarkStep(cur, crease);
    return cur;
}

/** Convert a cage to a triangle payload (quad faces split once). */
export function cageToTriangles(cage: SubdCage): { vertices: number[][]; indices: number[][] } {
    const indices: number[][] = [];
    cage.faces.forEach((f) => {
        if (f.length === 3) indices.push([f[0], f[1], f[2]]);
        else if (f.length >= 4) {
            for (let i = 1; i < f.length - 1; i++) indices.push([f[0], f[i], f[i + 1]]);
        }
    });
    return { vertices: cage.vertices.map((v) => [...v]), indices };
}

/**
 * Quad-ify a welded triangle mesh into a CC cage: pair triangles that
 * share two edges into quads where possible (edge-adjacent merge); the
 * remainder stays triangles (CC handles both). This is the ToSubD
 * extraction the old "weld and re-tag" fake never did.
 */
export function quadifyCage(tris: SubdCage): SubdCage {
    const edges = new Map<string, number[]>();
    tris.faces.forEach((f, fi) => {
        for (let i = 0; i < f.length; i++) {
            const x = f[i], y = f[(i + 1) % f.length];
            const k = x < y ? `${x}:${y}` : `${y}:${x}`;
            edges.set(k, [...(edges.get(k) ?? []), fi]);
        }
    });
    const used = new Set<number>();
    const faces: number[][] = [];
    tris.faces.forEach((f, fi) => {
        if (used.has(fi) || f.length !== 3) return;
        // try to merge with a neighbour sharing exactly one edge
        for (let i = 0; i < 3; i++) {
            const a = f[i], b = f[(i + 1) % 3];
            const k = a < b ? `${a}:${b}` : `${b}:${a}`;
            const nb = (edges.get(k) ?? []).find((x) => x !== fi && !used.has(x));
            if (nb === undefined) continue;
            const g = tris.faces[nb];
            const free = [...f.filter((x) => x !== a && x !== b), ...g.filter((x) => x !== a && x !== b)];
            if (free.length !== 2) continue;
            used.add(fi); used.add(nb);
            faces.push([free[0], a, free[1], b]);
            break;
        }
        if (!used.has(fi)) { used.add(fi); faces.push([...f]); }
    });
    return { vertices: tris.vertices.map((v) => [...v] as Pt3), faces };
}
